import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const BRISTOL_SCALE = [
  {
    value: 1,
    label: 'Type 1',
    name: 'Separate hard lumps',
    description: 'Severe constipation',
    color: '#B45050',
    lightBg: '#FFF1F1',
  },
  {
    value: 2,
    label: 'Type 2',
    name: 'Sausage-shaped, lumpy',
    description: 'Constipation',
    color: '#C66E3E',
    lightBg: '#FFF4ED',
  },
  {
    value: 3,
    label: 'Type 3',
    name: 'Sausage with cracks',
    description: 'Normal range',
    color: '#C68E3E',
    lightBg: '#FFF9EB',
  },
  {
    value: 4,
    label: 'Type 4',
    name: 'Smooth, soft sausage',
    description: 'Ideal',
    color: '#4A8C5C',
    lightBg: '#EFF8F2',
  },
  {
    value: 5,
    label: 'Type 5',
    name: 'Soft blobs, clear edges',
    description: 'Normal range',
    color: '#3A7A6A',
    lightBg: '#ECF8F5',
  },
  {
    value: 6,
    label: 'Type 6',
    name: 'Fluffy pieces, ragged',
    description: 'Mild diarrhea',
    color: '#3A6AA0',
    lightBg: '#EEF5FF',
  },
  {
    value: 7,
    label: 'Type 7',
    name: 'Watery, no solid',
    description: 'Diarrhea',
    color: '#6B4FA0',
    lightBg: '#F3EEFF',
  },
];

const getSelectedMeta = (value) => BRISTOL_SCALE.find((step) => step.value === value) || null;

const BristolStoolScaleSelector = ({
  value,
  onChange,
  className = '',
  label = 'Bristol stool scale',
  unselectedHint = 'Select a type if you want to track stool consistency',
}) => {
  const selectedStep = getSelectedMeta(value);

  return (
    <Stack spacing={1.25} className={className} sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1A2320', lineHeight: 1.2 }}>
          {label}
        </Typography>
        {selectedStep ? (
          <Typography sx={{ fontSize: '12px', fontWeight: 650, color: selectedStep.color, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {`${selectedStep.label} — ${selectedStep.description}`}
          </Typography>
        ) : null}
      </Box>

      <Stack direction="row" spacing={0.75} sx={{ width: '100%' }}>
        {BRISTOL_SCALE.map((step) => {
          const active = value === step.value;
          const tinted = alpha(step.color, 0.1);
          return (
            <ButtonBase
              key={step.value}
              onClick={() => onChange?.(step.value)}
              aria-pressed={active}
              sx={{
                flex: '1 1 0%',
                minWidth: 0,
                minHeight: 58,
                px: 0.4,
                py: 0.9,
                borderRadius: '10px',
                border: '1.5px solid',
                borderColor: active ? step.color : '#E5ECE8',
                bgcolor: active ? tinted : '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.35,
                boxShadow: 'none',
                transition: 'all 160ms ease',
                '&:hover': {
                  borderColor: active ? step.color : '#D6E8E1',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: step.color,
                  opacity: active ? 1 : 0.4,
                }}
              />
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: active ? 700 : 600,
                  color: active ? step.color : '#4A5954',
                  lineHeight: 1,
                }}
              >
                {step.value}
              </Typography>
            </ButtonBase>
          );
        })}
      </Stack>

      {selectedStep ? (
        <Box
          sx={{
            mt: 1,
            p: '10px 14px',
            borderRadius: 2,
            border: `1px solid ${alpha(selectedStep.color, 0.2)}`,
            bgcolor: selectedStep.lightBg,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: selectedStep.color,
              }}
            />
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: selectedStep.color, lineHeight: 1.2 }}>
              {`${selectedStep.label} — ${selectedStep.name}`}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '12px', color: selectedStep.color, opacity: 0.8, lineHeight: 1.35 }}>
            {selectedStep.description}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: '12px', color: '#7A8A84', lineHeight: 1.35 }}>
          {unselectedHint}
        </Typography>
      )}
    </Stack>
  );
};

export default BristolStoolScaleSelector;
